/**
 * Lightweight performance telemetry into the existing analytics buffer (docs/PERF.md, dashboard /cheats/funnel):
 *   perf_startup      ms from navigation start to the first screen being interactive (once per launch)
 *   perf_lesson_open  ms from the tap to the lesson's first item painted
 *   perf_sample       every 5 min while visible: long tasks / min, sampled fps, JS heap (where reported), DOM nodes,
 *                     battery saver on/off, route
 * No timers run while the app is hidden; one 2 s rAF sample per 5 minutes otherwise.
 */
import { track } from './analytics';
import { isHidden, isLowPower } from './power';
import { isNative } from './platform';

/** Last values on `window.__skPerf` (read by e2e/perf.mjs; the analytics buffer may already have been sent). */
const expose = (k: string, v: number) => {
  if (typeof window !== 'undefined') ((window as unknown as { __skPerf?: Record<string, number> }).__skPerf ??= {})[k] = v;
};

const platform = isNative ? (/android/i.test(navigator.userAgent) ? 'android' : 'ios') : 'web';
const now = () => (typeof performance !== 'undefined' ? performance.now() : 0);

let started = false;
let startupSent = false;
let lastTap = 0;
let longTasks = 0;
let windowStart = now();

/** First screen of the app is on screen and the main thread is free: report startup once. */
export function markInteractive(screen: string) {
  if (startupSent || typeof window === 'undefined') return;
  startupSent = true;
  // Wait for the real screen (lazy screens show a loading placeholder first), then two frames + an idle slot ≈
  // painted and responsive.
  const t0 = now();
  const ready = () => !!document.querySelector('.app-root') && !document.querySelector('[data-testid="screen-loading"]');
  const wait = () => (ready() || now() - t0 > 15_000 ? go() : requestAnimationFrame(wait));
  const go = () => requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const painted = Math.round(now());
      // Interactive = painted AND the main thread got an idle slot; the idle wait (if any) is added.
      expose('startup', painted);
      const fire = () => track('perf_startup', { ms: painted, interactiveMs: Math.round(now()), screen, platform, low: isLowPower() });
      const ric = (window as unknown as { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
      if (ric) ric(fire, { timeout: 1500 });
      else setTimeout(fire, 0);
    }),
  );
  wait();
}

/** A lesson finished mounting its first item: tap → paint. */
export function markLessonOpen(miniUnitId: string, mountedAt: number) {
  const from = lastTap && mountedAt - lastTap < 5000 ? lastTap : mountedAt;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const ms = Math.round(now() - from);
      expose('lessonOpen', ms);
      track('perf_lesson_open', { ms, mu: miniUnitId.slice(0, 48), platform, low: isLowPower() });
    }),
  );
}

function sampleFps(): Promise<number> {
  return new Promise((res) => {
    let n = 0;
    const t0 = now();
    const f = () => {
      n++;
      if (now() - t0 < 2000) requestAnimationFrame(f);
      else res(Math.round((n * 1000) / (now() - t0)));
    };
    requestAnimationFrame(f);
  });
}

async function sample() {
  if (isHidden()) return;
  const mins = Math.max(0.5, (now() - windowStart) / 60_000);
  const lt = longTasks;
  longTasks = 0;
  windowStart = now();
  const fps = await sampleFps();
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
  track('perf_sample', {
    longTasksPerMin: Math.round((lt / mins) * 10) / 10,
    fps,
    ...(mem ? { heapMB: Math.round(mem.usedJSHeapSize / 2 ** 20) } : {}),
    domNodes: document.getElementsByTagName('*').length,
    route: location.pathname.split('/').slice(0, 2).join('/') || '/',
    low: isLowPower(),
    platform,
  });
}

/** Start listening (once, at app start). */
export function startPerfTelemetry() {
  if (started || typeof window === 'undefined') return;
  started = true;
  document.addEventListener('pointerdown', () => (lastTap = now()), { capture: true, passive: true });
  try {
    new PerformanceObserver((l) => (longTasks += l.getEntries().length)).observe({ type: 'longtask', buffered: false });
  } catch {
    /* Safari: no long task API */
  }
  setInterval(() => void sample(), 5 * 60_000);
}
